import React, { useEffect } from 'react';
import { Star } from 'lucide-react';
import { useMenuItemRatings } from '@/hooks/useMenuItemRatings';
import i18n from '@/i18n';

interface MenuItemRatingDisplayProps {
  menuItemId: string;
  compact?: boolean;
  showRateButton?: boolean;
  onRateClick?: () => void;
  ratingKey?: number; // Force re-render when ratings change
}

export const MenuItemRatingDisplay: React.FC<MenuItemRatingDisplayProps> = ({ 
  menuItemId, 
  compact = false,
  showRateButton = false,
  onRateClick,
  ratingKey = 0
}) => {
  const { ratings, loading, error, refetch } = useMenuItemRatings(menuItemId);
  
  // Get current language
  const currentLanguage = i18n.language || 'en';
  const isArabic = currentLanguage === 'ar';

  // Debug logging
  useEffect(() => {
    // console.log(`🎭 MenuItemRatingDisplay for ${menuItemId}:`, {
    //   ratings,
    //   loading,
    //   compact,
    //   showRateButton
    // });
  }, [menuItemId, ratings, loading, compact, showRateButton]);

  const renderStars = (rating: number, size: 'sm' | 'xs' | 'xxs' = 'xs') => {
    let starClass = 'h-3 w-3';
    if (size === 'sm') starClass = 'h-4 w-4';
    if (size === 'xxs') starClass = 'h-2.5 w-2.5';
    
    return (
      <div className="flex gap-0.5 flex-shrink-0">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${starClass} ${
              star <= Math.round(rating)
                ? 'text-yellow-500 fill-current'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  // Fallback translations
  const translations = {
    loading: isArabic ? 'جار التحميل...' : 'Loading...',
    rateThis: isArabic ? 'قيم' : 'Rate',
    noRatings: isArabic ? 'بلا تقييم' : 'No rating',
    tapToRate: isArabic ? 'اضغط للتقييم' : 'Tap to rate'
  };

  // Add error handling for network issues
  if (error) {
    console.error('Rating display error:', error);
    if (showRateButton && onRateClick) {
      return (
        <button
          onClick={onRateClick}
          className="flex items-center gap-0.5 text-xs text-blue-600 hover:text-blue-700 transition-colors w-full min-w-0 p-0.5 rounded bg-blue-50 hover:bg-blue-100"
        >
          <div className="flex gap-0 flex-shrink-0">
            {renderStars(0, 'xxs')}
          </div>
          <span className="font-medium text-[8px] truncate flex-shrink min-w-0 ml-0.5">
            {translations.rateThis}
          </span>
        </button>
      );
    }
    return (
      <div className="flex items-center gap-0.5 text-xs text-gray-400 w-full">
        <div className="flex gap-0 flex-shrink-0">
          {renderStars(0, 'xxs')}
        </div>
        <span className="text-[8px] truncate ml-0.5">{translations.noRatings}</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-0.5 animate-pulse w-full">
        <div className="flex gap-0 flex-shrink-0">
          {renderStars(0, 'xxs')}
        </div>
        <div className="h-2 bg-gray-200 rounded flex-1 ml-0.5"></div>
      </div>
    );
  }

  // PRIORITIZE showing existing ratings
  if (ratings.totalRatings > 0) {
    if (compact) {
      return (
        <div className="w-full min-w-0 overflow-hidden">
          {/* Ultra-compact single line */}
          <div className="flex items-center gap-0.5 text-xs text-gray-600 w-full min-w-0">
            <div className="flex gap-0 flex-shrink-0" style={{ minWidth: 'fit-content' }}>
              {renderStars(ratings.averageRating, 'xxs')}
            </div>
            <div className="flex items-center gap-0.5 flex-shrink min-w-0 overflow-hidden">
              <span className="font-medium text-[8px] flex-shrink-0 whitespace-nowrap">
                {ratings.averageRating.toFixed(1)}
              </span>
              <span className="text-gray-400 text-[8px] flex-shrink-0 whitespace-nowrap">
                ({ratings.totalRatings})
              </span>
            </div>
          </div>
          {/* Rate button on next line if space is really tight */}
          {showRateButton && onRateClick && (
            <button
              onClick={onRateClick}
              className="text-[7px] text-blue-600 hover:text-blue-700 transition-colors mt-0.5 block w-full text-left truncate leading-tight"
            >
              + {translations.tapToRate}
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="w-full min-w-0">
        <div className="flex items-center gap-2 mb-1 w-full min-w-0">
          {renderStars(ratings.averageRating, 'sm')}
          <span className="text-sm font-medium text-gray-700 truncate">
            {ratings.averageRating.toFixed(1)} ({ratings.totalRatings})
          </span>
        </div>
        {showRateButton && onRateClick && (
          <button
            onClick={onRateClick}
            className="text-xs text-blue-600 hover:text-blue-700 transition-colors"
          >
            + {translations.tapToRate}
          </button>
        )}
      </div>
    );
  }

  // No ratings exist - show ultra-compact rate button
  if (showRateButton && onRateClick) {
    return (
      <button
        onClick={onRateClick}
        className="flex items-center gap-0.5 text-xs text-blue-600 hover:text-blue-700 transition-colors w-full min-w-0 p-1 rounded bg-blue-50 hover:bg-blue-100 overflow-hidden"
        style={{ minHeight: '20px' }}
      >
        <div className="flex gap-0 flex-shrink-0" style={{ minWidth: 'fit-content' }}>
          {renderStars(0, 'xxs')}
        </div>
        <span className="font-medium text-[8px] truncate flex-shrink min-w-0 ml-0.5">
          {translations.rateThis}
        </span>
      </button>
    );
  }

  // Show empty state - ultra compact
  return (
    <div className="flex items-center gap-0.5 text-xs text-gray-400 w-full overflow-hidden">
      <div className="flex gap-0 flex-shrink-0" style={{ minWidth: 'fit-content' }}>
        {renderStars(0, 'xxs')}
      </div>
      <span className="text-[8px] truncate ml-0.5 flex-shrink min-w-0">
        {translations.noRatings}
      </span>
    </div>
  );
};
